import { useState, useEffect } from "react";
import { useSearchParams } from "@remix-run/react";
import rollbar from "~/utils/rollbar.client";

const sendSeekSignalWithRetry = async (seekToken: string, retries = 5): Promise<void> => {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await fetch("/seek/send_signal", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ seekToken }),
      });

      if (response.ok) {
        return; // Success, exit retry loop
      }

      // Non-OK response - throw to be handled by catch block
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    } catch (error) {
      const msg = "Failed to send application complete signal to SEEK";
      const attemptNumber = attempt + 1;
      console.error(msg, error, {
        seekToken,
        attempt: attemptNumber,
        totalAttempts: retries,
      });
      rollbar.error(msg, {
        error,
        seekToken,
        attempt: attemptNumber,
        totalAttempts: retries,
      });

      if (attempt === retries - 1) {
        return; // return if this is the last attempt
      }

      // Wait before retrying with exponential backoff, the delays are: 1s, 2s, 4s, 8s, 16s
      const delay = 1000 * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

const useSeekSignal = () => {
  const [searchParams] = useSearchParams();
  const [signalSent, setSignalSent] = useState(false);

  useEffect(() => {
    if (signalSent) {
      return;
    }

    const seekToken = searchParams.get("seek-token");
    if (seekToken) {
      // Only send signal if seek-token is present (indicates Seek was used for application)
      sendSeekSignalWithRetry(seekToken);
      setSignalSent(true);
    }
  }, [searchParams, signalSent]);
};

export default useSeekSignal;
